import _, {isString} from 'lodash';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import {
  findGlobalOrigin,
  globalWindow,
} from '../../../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {addQueryToPath} from '../../../../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {FrontConfig} from '../../../../../../../libs/shared/core/src/helpers/bundler/bundlerDefineConfig';
import {useInitialEffect} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {reportToBugsnag} from '../../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {IntegrationFileAttachment} from '../../../../../../helpers/integrationHelpers';
import {IntegrationModel} from '../../../../../../models/api/integrationModel';
import {openElectronAuthPopup} from '../../../../../../services/electron/electronBridgeOutbound';
import {openRelativePopupUrl} from '../../../../../../services/popups';

let lastListenerId = 0;

export function useDriveAttachments(
  onIntegrationAttachLinks: (arg: ReadonlyArray<IntegrationFileAttachment>) => void,
) {
  const popupRef = useRef<Window>();
  const listenerIdRef = useRef<number>();
  const globalOrigin = useMemo(() => findGlobalOrigin(), []);

  // Event handler for messages from the Google Drive popup.
  const onMessage = useCallback(
    (event: MessageEvent) => {
      const {origin, data} = event;

      // Check if this event is for us.
      if (origin !== globalOrigin) {
        return;
      }

      // Check if the payload is what we expect.
      if (!data || data.action !== 'pick' || data.listenerId !== listenerIdRef.current) {
        return;
      }

      // Parse the result.
      const integrationFiles = _(data.data.docs)
        .map(({name, url, mimeType}) => {
          if (!isString(name) || !isString(url)) {
            return undefined;
          }

          return {name, url, mimeType};
        })
        .compact()
        .value();

      onIntegrationAttachLinks(integrationFiles);
    },
    [globalOrigin, onIntegrationAttachLinks],
  );

  // Register our handler on the window.
  useEffect(() => {
    globalWindow.addEventListener('message', onMessage, false);
    return () => globalWindow.removeEventListener('message', onMessage);
  }, [onMessage]);

  // Ensure the popup is cleaned-up once closed.
  useInitialEffect(() => () => {
    try {
      const currentPopup = popupRef.current;
      if (!currentPopup) {
        return;
      }

      currentPopup.close();
    } catch (error) {
      reportToBugsnag(error);
    }

    popupRef.current = undefined;
    listenerIdRef.current = undefined;
  });

  // Function to display the picker.
  const showDriveAttachmentsPicker = useCallback(
    ({typeNoAuthUrl}: IntegrationModel) => {
      // If a picker is already open, do nothing.
      if (popupRef.current && !popupRef.current.closed) {
        return;
      }

      listenerIdRef.current = ++lastListenerId;
      const url = `${typeNoAuthUrl}/public/templates/drive.html`;
      const query = {origin: globalOrigin, listenerId: listenerIdRef.current, env: FrontConfig.FRONT_ENV};
      if (openElectronAuthPopup(addQueryToPath(url, query))) {
        return;
      }

      const popup = openRelativePopupUrl(url, {
        width: 800,
        height: 600,
        query,
      });
      if (!popup) {
        return;
      }

      popupRef.current = popup;
    },
    [globalOrigin],
  );

  return {showDriveAttachmentsPicker};
}
